import { validateEngineInput } from "../contracts/validate";
import type { DatasetRecord, EngineInput, EngineOutput, Finding, MaskRuleConfig, RunStatus } from "../contracts/types";
import { SCHEMA_VERSION } from "../contracts/types";
import { parseDataset } from "./dataset-parse";
import { profileDataset } from "./profile";
import type { FieldProfile } from "./profile";
import { applyMaskValue, createMaskContext } from "./mask";
import { previewDiff } from "./diff";
import type { DiffResult } from "./diff";
import { computeCoverage } from "./coverage";
import type { CoverageSummary } from "./coverage";
import { LIMITS } from "./limits";
import { fingerprintPayload, makeRunId } from "./ids";

export const RULES_VERSION = "1.0.0";

export interface AnonymizationResult {
  output: EngineOutput;
  fieldProfiles: FieldProfile[];
  effectiveRules: MaskRuleConfig[];
  originalRecords: DatasetRecord[];
  maskedRecords: DatasetRecord[];
  fieldOrder: string[];
  diff: DiffResult;
  coverage: CoverageSummary;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractScenarioId(rawInput: unknown): string {
  if (isPlainObject(rawInput) && typeof rawInput.scenarioId === "string") return rawInput.scenarioId;
  return "unknown";
}

/**
 * Resuelve la regla efectiva de cada campo: la explícita de `payload.rules`
 * si existe, o "preserve" en caso contrario.
 *
 * DELIBERADAMENTE el motor NO aplica una regla de máscara automáticamente
 * solo por detectar un candidato: la detección (P13-R1) y el enmascarado
 * (P13-R2) son pasos separados y el segundo requiere una decisión explícita
 * (del visitante, vía `payload.rules`, o suya propia en la UI). Si el motor
 * enmascarara todo automáticamente, el informe de cobertura (P13-R4) y los
 * hallazgos "PII_NOT_MASKED"/riesgo de combinación nunca podrían demostrar
 * nada — siempre mostrarían 100%. Esto es intencional: refuerza el aviso de
 * que la herramienta no actúa "mágicamente" por defecto sobre los datos.
 */
export function resolveRules(fieldProfiles: FieldProfile[], explicitRules: MaskRuleConfig[]): MaskRuleConfig[] {
  const explicitByField = new Map(explicitRules.map((rule) => [rule.field, rule]));
  return fieldProfiles.map(
    (profile) => explicitByField.get(profile.name) ?? { field: profile.name, strategy: "preserve" as const },
  );
}

function maskAll(
  records: DatasetRecord[],
  fieldOrder: string[],
  rules: MaskRuleConfig[],
  runSeed: string,
): DatasetRecord[] {
  const ctx = createMaskContext(runSeed);
  const ruleByField = new Map(rules.map((rule) => [rule.field, rule]));
  return records.map((record, rowIndex) => {
    const masked: DatasetRecord = {};
    for (const field of fieldOrder) {
      const rule = ruleByField.get(field);
      const value = record[field] ?? null;
      masked[field] = rule ? applyMaskValue(value, field, rule.strategy, rule.param, rowIndex, ctx) : value;
    }
    return masked;
  });
}

function truncateFindings(findings: Finding[]): Finding[] {
  if (findings.length <= LIMITS.maxFindings) return findings;
  const truncated = findings.slice(0, LIMITS.maxFindings - 1);
  truncated.push({
    ruleId: "FINDINGS_TRUNCATED",
    severity: "warning",
    message: `Se truncó la lista de hallazgos a ${LIMITS.maxFindings - 1} de ${findings.length} totales.`,
  });
  return truncated;
}

function emptyDiff(): DiffResult {
  return { rows: [], summary: { totalRows: 0, previewRows: 0, totalCells: 0, changedCells: 0, changedFields: [] } };
}

function emptyCoverage(): CoverageSummary {
  return {
    perField: [],
    overall: { relevant: 0, covered: 0, percentage: 100 },
    uncoveredFields: [],
    combinationRisk: false,
    combinationRiskFields: [],
  };
}

function buildOutput(runId: string, status: RunStatus, summary: string, findings: Finding[], scenarioId: string): EngineOutput {
  return {
    schemaVersion: SCHEMA_VERSION,
    runId,
    status,
    summary,
    findings: truncateFindings(findings),
    evidence: { rulesVersion: RULES_VERSION, scenarioId },
  };
}

function failedResult(scenarioId: string, findings: Finding[], summary: string): AnonymizationResult {
  const runId = makeRunId(scenarioId, fingerprintPayload(scenarioId));
  return {
    output: buildOutput(runId, "failed", summary, findings, scenarioId),
    fieldProfiles: [],
    effectiveRules: [],
    originalRecords: [],
    maskedRecords: [],
    fieldOrder: [],
    diff: emptyDiff(),
    coverage: emptyCoverage(),
  };
}

/** Descargo de responsabilidad permanente: mitiga el riesgo "falsa sensación de anonimato". */
const DISCLAIMER_FINDING: Finding = {
  ruleId: "ANONYMIZATION_NOT_CERTIFIED",
  severity: "info",
  message:
    "Esta herramienta aplica transformaciones configurables sobre los datos. NO certifica anonimización real ni protege por sí sola frente a reidentificación por combinación de campos (k-anonimato, ataques de vinculación, etc.). La responsabilidad de validar el resultado es de quien lo usa.",
};

function buildRiskFindings(fieldProfiles: FieldProfile[], coverage: CoverageSummary): Finding[] {
  const findings: Finding[] = [DISCLAIMER_FINDING];

  for (const field of coverage.uncoveredFields) {
    const profile = fieldProfiles.find((candidate) => candidate.name === field);
    findings.push({
      ruleId: "PII_NOT_MASKED",
      severity: profile?.confidence === "high" ? "critical" : "error",
      message: `El campo "${field}" se detectó como candidato a dato personal (${profile?.category ?? "desconocida"}, confianza ${profile?.confidence ?? "media"}) pero no tiene una regla de máscara activa.`,
      evidencePath: `#/payload/records/*/${field}`,
      suggestion: "Configura una regla de máscara (hash, randomize, redact o generalize) para este campo antes de exportar.",
    });
  }

  if (coverage.combinationRisk) {
    findings.push({
      ruleId: "REIDENTIFICATION_COMBINATION_RISK",
      severity: "warning",
      message: `Los campos ${coverage.combinationRiskFields.join(", ")} son cuasi-identificadores y permanecen sin enmascarar a la vez; combinarlos puede permitir reidentificar filas aunque cada campo por separado no sea sensible.`,
      suggestion: "Enmascara o generaliza al menos uno de estos campos para reducir el riesgo de combinación.",
    });
  }

  for (const profile of fieldProfiles) {
    if (profile.piiCandidate && profile.confidence === "medium") {
      findings.push({
        ruleId: "REVIEW_SUGGESTED",
        severity: "warning",
        message: `El campo "${profile.name}" se detectó con confianza media (${profile.reason}). Revisa manualmente si corresponde tratarlo como dato personal.`,
        evidencePath: `#/payload/records/*/${profile.name}`,
      });
    }
  }

  return findings;
}

/**
 * Punto de entrada puro del motor de dominio (07-contratos-interfaces.md:
 * `profileDataset` + `applyRules` combinados). Recibe la envolvente de
 * entrada, valida, interpreta el dataset y produce el perfil de campos, el
 * dataset enmascarado, la vista previa/diff y el informe de cobertura. Nunca
 * lanza excepciones: cualquier problema se traduce en un EngineOutput con
 * status "failed" y findings tipados.
 */
export function generateAnonymization(
  rawInput: unknown,
  options: { shouldCancel?: () => boolean } = {},
): AnonymizationResult {
  const scenarioId = extractScenarioId(rawInput);
  const validation = validateEngineInput(rawInput);
  if (!validation.valid) {
    const findings: Finding[] = validation.errors.map((error) => ({
      ruleId: error.code,
      severity: "critical",
      message: error.message,
      evidencePath: error.path,
    }));
    return failedResult(scenarioId, findings, "La entrada no cumple el contrato input.schema.json.");
  }

  const input = rawInput as EngineInput;
  const findings: Finding[] = [];

  if (!input.options.deterministic) {
    findings.push({
      ruleId: "ADAPTER_DISABLED",
      severity: "info",
      message:
        "El adaptador real está deshabilitado por diseño (kill switch permanente en este MVP). Se usó el motor determinista local sin llamadas de red.",
    });
  }

  const parsed = parseDataset(input.payload);
  for (const warning of parsed.warnings) {
    findings.push({ ruleId: "DATASET_WARNING", severity: "warning", message: warning, evidencePath: "#/payload" });
  }
  if (parsed.errors.length > 0) {
    for (const error of parsed.errors) {
      findings.push({ ruleId: "INPUT_INVALID", severity: "critical", message: error, evidencePath: "#/payload" });
    }
    return failedResult(input.scenarioId, findings, "El dataset no se pudo interpretar de forma segura.");
  }

  const runId = makeRunId(input.scenarioId, fingerprintPayload(input.payload));

  if (options.shouldCancel?.()) {
    findings.push({ ruleId: "RUN_CANCELLED", severity: "info", message: "Ejecución cancelada por el visitante." });
    return {
      output: buildOutput(runId, "cancelled", "El visitante canceló la ejecución antes de completarla.", findings, input.scenarioId),
      fieldProfiles: [],
      effectiveRules: [],
      originalRecords: [],
      maskedRecords: [],
      fieldOrder: parsed.fieldOrder,
      diff: emptyDiff(),
      coverage: emptyCoverage(),
    };
  }

  const knownFields = new Set(parsed.fieldOrder);
  for (const rule of parsed.rules) {
    if (!knownFields.has(rule.field)) {
      findings.push({
        ruleId: "RULE_UNKNOWN_FIELD",
        severity: "warning",
        message: `La regla configurada para "${rule.field}" no coincide con ningún campo del dataset.`,
        evidencePath: "#/payload/rules",
      });
    }
  }

  const fieldProfiles = profileDataset(parsed.records, parsed.fieldOrder);
  const effectiveRules = resolveRules(fieldProfiles, parsed.rules);
  const maskedRecords = maskAll(parsed.records, parsed.fieldOrder, effectiveRules, runId);
  const diff = previewDiff(parsed.records, maskedRecords, parsed.fieldOrder);
  const coverage = computeCoverage(fieldProfiles, effectiveRules);

  findings.push(...buildRiskFindings(fieldProfiles, coverage));

  const status: RunStatus = parsed.records.length === 0 ? "partial" : "completed";
  const summary =
    parsed.records.length === 0
      ? "Ejecución completada sin filas que procesar."
      : `Se perfilaron ${fieldProfiles.length} campo(s) sobre ${parsed.records.length} fila(s). Cobertura de campos candidatos: ${coverage.overall.percentage}% (${coverage.overall.covered}/${coverage.overall.relevant}).`;

  return {
    output: buildOutput(runId, status, summary, findings, input.scenarioId),
    fieldProfiles,
    effectiveRules,
    originalRecords: parsed.records,
    maskedRecords,
    fieldOrder: parsed.fieldOrder,
    diff,
    coverage,
  };
}

/**
 * Recalcula el enmascarado tras un cambio manual de regla en un campo
 * concreto (recorrido de 90s: "cambia un parámetro y demuestra resultado
 * distinto"). Es una función pura y barata: no vuelve a perfilar el dataset
 * ni requiere pasar de nuevo por el Web Worker.
 */
export function overrideFieldRule(
  originalRecords: DatasetRecord[],
  fieldOrder: string[],
  fieldProfiles: FieldProfile[],
  effectiveRules: MaskRuleConfig[],
  runId: string,
  field: string,
  patch: Partial<Pick<MaskRuleConfig, "strategy" | "param">>,
): { effectiveRules: MaskRuleConfig[]; maskedRecords: DatasetRecord[]; diff: DiffResult; coverage: CoverageSummary } {
  const nextRules = effectiveRules.map((rule) => (rule.field === field ? { ...rule, ...patch } : rule));
  const maskedRecords = maskAll(originalRecords, fieldOrder, nextRules, runId);
  const diff = previewDiff(originalRecords, maskedRecords, fieldOrder);
  const coverage = computeCoverage(fieldProfiles, nextRules);
  return { effectiveRules: nextRules, maskedRecords, diff, coverage };
}
